import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Crown, CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let attempts = 0;
    let active = true;

    const poll = async () => {
      if (!active) return;
      attempts += 1;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          await refreshUser();
          setStatus("paid");
          return;
        }
        if (["failed", "expired"].includes(res.data.status)) {
          setStatus("failed");
          return;
        }
      } catch (e) {
        /* keep polling */
      }
      if (attempts >= 12) {
        setStatus("failed");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      active = false;
    };
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#090A0F] grid-bg flex items-center justify-center px-4">
      <div className="w-full max-w-md text-center bg-[#12141D] border border-white/10 rounded-3xl p-10 shadow-2xl animate-float-in">
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-fuchsia-400 mx-auto animate-spin" />
            <h1 className="font-display text-2xl font-bold text-white mt-6">Zahlung wird bestätigt...</h1>
            <p className="text-slate-400 text-sm mt-2">Einen Moment, wir aktivieren dein VIP.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-500 to-fuchsia-600 flex items-center justify-center mx-auto shadow-lg shadow-fuchsia-500/30">
              <Crown className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-display text-2xl font-bold text-white mt-6" data-testid="payment-success">
              Willkommen im VIP! 🎉
            </h1>
            <p className="text-slate-400 text-sm mt-2">
              Du hast jetzt unbegrenzte Nachrichten und Zugriff auf alle VIP-Charaktere.
            </p>
            <button
              data-testid="success-explore-btn"
              onClick={() => navigate("/")}
              className="mt-8 w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-semibold rounded-xl py-3 active:scale-95 transition-all"
            >
              VIP-Charaktere entdecken
            </button>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle className="w-12 h-12 text-rose-400 mx-auto" />
            <h1 className="font-display text-2xl font-bold text-white mt-6">Zahlung nicht bestätigt</h1>
            <p className="text-slate-400 text-sm mt-2">
              Es hat etwas länger gedauert oder wurde abgebrochen. Bitte versuche es erneut.
            </p>
            <button
              onClick={() => navigate("/vip")}
              className="mt-8 w-full bg-white/10 hover:bg-white/15 border border-white/10 text-white font-semibold rounded-xl py-3 active:scale-95 transition-all"
            >
              Zurück zur VIP-Seite
            </button>
          </>
        )}
        <div className="mt-4">
          <CheckCircle2 className="hidden" />
        </div>
      </div>
    </div>
  );
}
